import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { BookOpen, Eye, X, Loader2, Save, ChevronUp, ChevronDown, Trash2, Scissors, Plus, Check, Search, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { useNotification } from '@/contexts/NotificationContext';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const OutlineViewerModalV3 = ({ isOpen, onClose, manuscriptId, onSaveAndContinue, isStandalone = false }) => {
  const { notifyError } = useNotification();
  const [outline, setOutline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segmentLoading, setSegmentLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedSegments, setEditedSegments] = useState([]);
  const [originalSegments, setOriginalSegments] = useState([]); // Track original state for change detection
  const [isEditingText, setIsEditingText] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [savingText, setSavingText] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [merging, setMerging] = useState(false);
  const [splitting, setSplitting] = useState(false);
  // Click-to-split mode state
  const [isClickToSplitMode, setIsClickToSplitMode] = useState(false);
  const [splitPoints, setSplitPoints] = useState([]); // Array of { charIndex, label }
  const [pendingSplitIndex, setPendingSplitIndex] = useState(null); // Character index waiting for label
  const [tempSplitLabel, setTempSplitLabel] = useState('');
  const [adjustingSplitIndex, setAdjustingSplitIndex] = useState(null); // Which split is being adjusted
  const [showFirstSegmentPrompt, setShowFirstSegmentPrompt] = useState(false); // Show prompt for first segment name
  const [firstSegmentLabel, setFirstSegmentLabel] = useState(''); // First segment label
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]); // Array of { index, charIndex }
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const textContainerRef = useRef(null);
  // Track unsaved changes and auto-save
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // 'saving', 'saved', 'error'
  const autoSaveTimeoutRef = useRef(null);
  const editedSegmentsRef = useRef(editedSegments);
  
  // Keep ref in sync with state
  useEffect(() => {
    editedSegmentsRef.current = editedSegments;
  }, [editedSegments]);

  useEffect(() => {
    if (isOpen && manuscriptId) {
      fetchOutline();
      // Reset state when modal opens
      setHasUnsavedChanges(false);
      setLastSaveTime(null);
      setSaveSuccess(false);
      setAutoSaveStatus(null);
    }
  }, [isOpen, manuscriptId]);
  
  // AUTO-SAVE: Save automatically every 2 minutes when there are unsaved changes
  useEffect(() => {
    if (!isOpen || !manuscriptId || !hasUnsavedChanges) {
      return;
    }
    
    // Clear any existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Set auto-save for 2 minutes from now
    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (!editedSegmentsRef.current || editedSegmentsRef.current.length === 0) return;
      
      console.log('[AUTO-SAVE] Triggering auto-save...');
      setAutoSaveStatus('saving');
      
      try {
        const token = localStorage.getItem('accessToken');
        const payload = {
          segments: editedSegmentsRef.current.map(seg => ({
            id: seg.id,
            logical_type: seg.logical_type,
            custom_label: seg.label !== seg.original_label ? seg.label : null,
            // P0: include_in_tts REMOVED - deletion is the only exclusion
            audio_order: seg.audio_order ?? seg.order,
            is_deleted: seg.is_deleted || false
          }))
        };
        
        // P0.3: Log payload segment count
        const payloadActiveCount = payload.segments.filter(s => !s.is_deleted).length;
        const payloadDeletedCount = payload.segments.filter(s => s.is_deleted).length;
        console.log(`[OUTLINE V3] Save payload segment_count: ${payloadActiveCount} (${payloadDeletedCount} marked deleted)`);
        
        const response = await axios.patch(`${API}/v3/manuscripts/${manuscriptId}/outline`, payload, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        });
        
        // P0.3: Log response segment count
        console.log(`[OUTLINE V3] Save response segment_count: ${response.data.outline_saved_segment_count}`);
        console.log(`[OUTLINE V3] Save response removed_count: ${response.data.outline_removed_count}`);
        console.log(`[OUTLINE V3] Save response hash: ${response.data.outline_saved_hash}`);
        
        console.log('[AUTO-SAVE] Success');
        setAutoSaveStatus('saved');
        setHasUnsavedChanges(false);
        setLastSaveTime(new Date());
        
        // Clear status after 3 seconds
        setTimeout(() => setAutoSaveStatus(null), 3000);
        
      } catch (error) {
        console.error('[AUTO-SAVE] Failed:', error);
        setAutoSaveStatus('error');
      }
    }, 120000); // 2 minutes
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [isOpen, manuscriptId, hasUnsavedChanges]);
  
  // LOCAL BACKUP: Save to localStorage as backup every time changes are made
  useEffect(() => {
    if (!isOpen || !manuscriptId || !hasUnsavedChanges || editedSegments.length === 0) {
      return;
    }
    
    const backupKey = `outline_v3_backup_${manuscriptId}`;
    const backup = {
      timestamp: Date.now(),
      segments: editedSegments.map(seg => ({
        id: seg.id,
        audio_order: seg.audio_order,
        label: seg.label
      }))
    };
    
    try {
      localStorage.setItem(backupKey, JSON.stringify(backup));
      console.log('[LOCAL BACKUP] Saved to localStorage');
    } catch (e) {
      console.warn('[LOCAL BACKUP] Failed to save:', e);
    }
  }, [isOpen, manuscriptId, hasUnsavedChanges, editedSegments]);
  
  // Clear backup after successful save
  const clearLocalBackup = () => {
    if (manuscriptId) {
      localStorage.removeItem(`outline_v3_backup_${manuscriptId}`);
    }
  };

  const fetchOutline = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/v3/manuscripts/${manuscriptId}/outline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[OUTLINE V3] Outline received:', response.data);
      setOutline(response.data);
      const segments = response.data.segments || [];
      // Initialize edited segments with current data and sort by audio_order (playback sequence)
      // audio_order controls playback sequence, while order is the immutable NFD record index
      const editedSegments = segments
        .map(seg => ({
          id: seg.id,
          label: seg.label,
          original_label: seg.original_label,
          logical_type: seg.logical_type,
          // P0: include_in_tts REMOVED - all active segments are eligible
          word_count: seg.word_count,
          character_count: seg.character_count,
          order: seg.order,  // Immutable NFD record index
          audio_order: seg.audio_order ?? seg.order,  // Playback sequence (user can reorder)
          has_audio: seg.has_audio || false,  // Track if segment has audio
          // P0.7 FIX: Preserve is_deleted, is_removed, removed_at from backend
          is_deleted: seg.is_deleted || seg.is_removed || !!seg.removed_at || false
        }))
        .sort((a, b) => (a.audio_order ?? a.order) - (b.audio_order ?? b.order)); // Sort by audio_order for playback
      
      // P0.7: Filter to active segments only for display count
      const activeSegments = editedSegments.filter(s => !s.is_deleted);
      
      // P0.3: Log refetch segment count - use active count, not total
      console.log(`[OUTLINE V3] Refetch segment_count after save: ${activeSegments.length} active, ${editedSegments.length} total`);
      console.log('[OUTLINE V3] Segments sorted by audio_order:', editedSegments.map(s => ({ id: s.id, order: s.order, audio_order: s.audio_order, label: s.label, is_deleted: s.is_deleted })));
      setEditedSegments(editedSegments);
      // Store deep copy of original segments for change detection
      setOriginalSegments(JSON.parse(JSON.stringify(editedSegments)));
    } catch (error) {
      console.error('[OUTLINE V3] Error fetching outline:', error);
      // Handle structured error responses from backend
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.message || errorDetail.error || 'Failed to load outline')
        : (errorDetail || 'Failed to load outline');
      notifyError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchSegmentText = async (segmentId) => {
    setSegmentLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/v3/manuscripts/${manuscriptId}/segments/${segmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSegment(response.data);
    } catch (error) {
      console.error('[SEGMENT V3] Error fetching segment:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.message || errorDetail.error || 'Failed to load segment text')
        : (errorDetail || 'Failed to load segment text');
      notifyError(errorMessage);
    } finally {
      setSegmentLoading(false);
    }
  };

  const handleSegmentClick = (segment) => {
    fetchSegmentText(segment.id);
    setIsEditingText(false);
  };

  const handleCloseSegmentView = () => {
    setSelectedSegment(null);
    setIsEditingText(false);
    setEditedText('');
  };

  const handleStartEditingText = () => {
    setIsEditingText(true);
    setEditedText(selectedSegment.full_text);
  };

  const handleCancelEditingText = () => {
    setIsEditingText(false);
    setEditedText('');
  };

  const handleSaveSegmentText = async () => {
    if (!selectedSegment || !editedText) return;
    
    setSavingText(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.patch(
        `${API}/v3/manuscripts/${manuscriptId}/segments/${selectedSegment.id}`,
        { full_text: editedText },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      console.log('[SEGMENT EDIT] Segment updated:', response.data);
      
      // Update local state
      setSelectedSegment({
        ...selectedSegment,
        full_text: editedText,
        word_count: response.data.word_count,
        character_count: response.data.character_count,
        manually_edited: true
      });
      
      // Update in editedSegments array for change detection
      setEditedSegments(prev => 
        prev.map(seg => 
          seg.id === selectedSegment.id
            ? {
                ...seg,
                word_count: response.data.word_count,
                character_count: response.data.character_count,
                manually_edited: true,
                has_audio: false  // Text changed, audio needs regeneration
              }
            : seg
        )
      );
      
      setIsEditingText(false);
      // UI shows updated segment - no toast needed
      console.log(`[SEGMENT EDIT] Updated (${response.data.character_diff > 0 ? '+' : ''}${response.data.character_diff} characters)`);
      
    } catch (error) {
      console.error('[SEGMENT EDIT] Error saving:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.message || errorDetail.error || 'Failed to save segment text')
        : (errorDetail || 'Failed to save segment text');
      notifyError(errorMessage);
    } finally {
      setSavingText(false);
    }
  };

  const toggleSegmentSelection = (segmentId) => {
    setSelectedSegments(prev => {
      if (prev.includes(segmentId)) {
        return prev.filter(id => id !== segmentId);
      } else {
        return [...prev, segmentId];
      }
    });
  };

  const handleMergeSegments = async () => {
    if (selectedSegments.length < 2) {
      notifyError('Please select at least 2 segments to merge');
      return;
    }
    
    setMerging(true);
    try {
      const token = localStorage.getItem('accessToken');
      
      // Get labels of selected segments for confirmation
      const segLabels = selectedSegments.map(id => {
        const seg = editedSegments.find(s => s.id === id);
        return seg?.label || id;
      }).join(', ');
      
      const confirmed = window.confirm(
        `Merge ${selectedSegments.length} segments?\n\n${segLabels}\n\nThis will combine their text and mark originals as deleted.`
      );
      
      if (!confirmed) {
        setMerging(false);
        return;
      }
      
      const response = await axios.post(
        `${API}/v3/manuscripts/${manuscriptId}/segments/merge`,
        { segment_ids: selectedSegments },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      console.log('[MERGE] Segments merged:', response.data);
      
      // Outline refreshes to show merged - no toast needed
      
      // Clear selection
      setSelectedSegments([]);
      
      // Refresh outline to show merged segment
      await fetchOutline();
      
    } catch (error) {
      console.error('[MERGE] Error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.message || errorDetail.error || 'Failed to merge segments')
        : (errorDetail || 'Failed to merge segments');
      notifyError(errorMessage);
    } finally {
      setMerging(false);
    }
  };

  // Enter click-to-split mode
  const handleEnterClickToSplitMode = () => {
    if (!selectedSegment?.full_text) return;
    setSplitPoints([]);
    setPendingSplitIndex(null);
    setTempSplitLabel('');
    setSearchQuery('');
    setSearchResults([]);
    setCurrentSearchIndex(0);
    setIsClickToSplitMode(true);
    console.log(`[Click-to-Split] Entered mode with ${selectedSegment.full_text.length} chars`);
  };

  // Exit click-to-split mode
  const handleExitClickToSplitMode = () => {
    setIsClickToSplitMode(false);
    setSplitPoints([]);
    setPendingSplitIndex(null);
    setTempSplitLabel('');
    setSearchQuery('');
    setSearchResults([]);
    setCurrentSearchIndex(0);
  };

  // Search functions
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    
    if (!query.trim() || !selectedSegment?.full_text) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }
    
    const text = selectedSegment.full_text.toLowerCase();
    const searchTerm = query.toLowerCase();
    const results = [];
    let pos = 0;
    
    while ((pos = text.indexOf(searchTerm, pos)) !== -1) {
      results.push({ charIndex: pos });
      pos += 1;
    }
    
    setSearchResults(results);
    setCurrentSearchIndex(0);
    
    if (results.length > 0) {
      scrollToSearchResult(results[0].charIndex);
    }
    
    console.log(`[Search] Found ${results.length} matches for "${query}"`);
  }, [selectedSegment?.full_text]);

  const scrollToSearchResult = (charIndex) => {
    if (!textContainerRef.current || !selectedSegment?.full_text) return;
    
    // Estimate scroll position based on character index
    const text = selectedSegment.full_text;
    const totalChars = text.length;
    const containerHeight = textContainerRef.current.scrollHeight;
    const scrollRatio = charIndex / totalChars;
    const scrollTarget = containerHeight * scrollRatio;
    
    textContainerRef.current.scrollTo({
      top: Math.max(0, scrollTarget - 100), // Offset a bit to show context
      behavior: 'smooth'
    });
  };

  const handleNextSearchResult = () => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    scrollToSearchResult(searchResults[nextIndex].charIndex);
  };

  const handlePrevSearchResult = () => {
    if (searchResults.length === 0) return;
    const prevIndex = (currentSearchIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentSearchIndex(prevIndex);
    scrollToSearchResult(searchResults[prevIndex].charIndex);
  };

  // Handle click on text to add split point
  const handleTextClick = (e) => {
    if (!isClickToSplitMode || !selectedSegment?.full_text) return;
    
    const text = selectedSegment.full_text;
    
    // Get selection info
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    // Get the selected text or caret position
    const selectedText = selection.toString();
    
    // If user selected some text, find it in the document
    if (selectedText && selectedText.length > 3) {
      const foundIndex = text.indexOf(selectedText);
      if (foundIndex !== -1) {
        // Snap to word boundary before the selection
        const charIndex = findWordBoundary(text, foundIndex, 'backward');
        
        // Check if there's already a split near this position
        const nearbyExists = splitPoints.some(sp => Math.abs(sp.charIndex - charIndex) < 50);
        if (nearbyExists) {
          notifyError('A split point already exists near this location');
          return;
        }
        
        setPendingSplitIndex(charIndex);
        setTempSplitLabel(`Chapter ${splitPoints.length + 2}`);
        console.log(`[Click-to-Split] Found selection at char ${charIndex}`);
        return;
      }
    }
    
    // Fallback: use caret position from range
    const range = selection.getRangeAt(0);
    const textContainer = e.currentTarget;
    
    // Find the click position relative to the ORIGINAL text only
    // We need to skip marker elements (split badges) when calculating position
    let charIndex = 0;
    const walker = document.createTreeWalker(textContainer, NodeFilter.SHOW_TEXT, null, false);
    let node;
    let found = false;
    
    while ((node = walker.nextNode())) {
      // Check if this text node is inside a split marker (data-split-marker attribute on parent)
      const isMarkerText = node.parentElement?.closest('[data-split-marker="true"]');
      
      if (node === range.startContainer) {
        // Only add offset if we're in a non-marker text node
        if (!isMarkerText) {
          charIndex += range.startOffset;
        }
        found = true;
        break;
      }
      
      // Only count text from non-marker elements
      if (!isMarkerText) {
        const nodeText = node.textContent || '';
        charIndex += nodeText.length;
      }
    }
    
    if (!found || charIndex <= 0 || charIndex >= text.length - 1) {
      return;
    }
    
    // Snap to nearest word boundary
    charIndex = findWordBoundary(text, charIndex, 'nearest');
    
    // Check if there's already a split near this position
    const nearbyExists = splitPoints.some(sp => Math.abs(sp.charIndex - charIndex) < 50);
    if (nearbyExists) {
      notifyError('A split point already exists near this location');
      return;
    }
    
    setPendingSplitIndex(charIndex);
    setTempSplitLabel(`Chapter ${splitPoints.length + 2}`);
    
    const contextStart = Math.max(0, charIndex - 30);
    const contextEnd = Math.min(text.length, charIndex + 30);
    const context = text.substring(contextStart, contextEnd);
    console.log(`[Click-to-Split] Click at char ${charIndex}: "...${context}..."`);
  };

  // Find word boundary in text
  const findWordBoundary = (text, position, direction) => {
    if (direction === 'backward' || direction === 'nearest') {
      // Move backward to find start of word (space or newline)
      let pos = position;
      while (pos > 0 && !/\s/.test(text[pos - 1])) {
        pos--;
      }
      if (direction === 'backward') return pos;
      
      // For 'nearest', also check forward
      let forwardPos = position;
      while (forwardPos < text.length && !/\s/.test(text[forwardPos])) {
        forwardPos++;
      }
      // Skip the whitespace to get to next word
      while (forwardPos < text.length && /\s/.test(text[forwardPos])) {
        forwardPos++;
      }
      
      // Return whichever is closer
      return (position - pos) <= (forwardPos - position) ? pos : forwardPos;
    }
    
    if (direction === 'forward') {
      // Move forward past current word, then past whitespace
      let pos = position;
      while (pos < text.length && !/\s/.test(text[pos])) {
        pos++;
      }
      while (pos < text.length && /\s/.test(text[pos])) {
        pos++;
      }
      return pos;
    }
    
    return position;
  };

  // Find next/previous word boundary from current position
  const findNextWordBoundary = (text, position) => {
    let pos = position;
    // Skip current word
    while (pos < text.length && !/\s/.test(text[pos])) {
      pos++;
    }
    // Skip whitespace
    while (pos < text.length && /\s/.test(text[pos])) {
      pos++;
    }
    return Math.min(pos, text.length - 1);
  };

  const findPrevWordBoundary = (text, position) => {
    let pos = position;
    // Move back past any whitespace
    while (pos > 0 && /\s/.test(text[pos - 1])) {
      pos--;
    }
    // Move back past the word
    while (pos > 0 && !/\s/.test(text[pos - 1])) {
      pos--;
    }
    return Math.max(pos, 1);
  };

  // Confirm split point with label
  const handleConfirmSplit = () => {
    if (pendingSplitIndex === null || !tempSplitLabel.trim()) {
      notifyError('Please enter a name for this segment');
      return;
    }
    
    // Ensure we're at a word boundary
    const text = selectedSegment?.full_text || '';
    const snappedIndex = findWordBoundary(text, pendingSplitIndex, 'nearest');
    
    const newSplit = {
      charIndex: snappedIndex,
      label: tempSplitLabel.trim()
    };
    
    setSplitPoints(prev => [...prev, newSplit].sort((a, b) => a.charIndex - b.charIndex));
    setPendingSplitIndex(null);
    setTempSplitLabel('');
    console.log('[SUCCESS] ' + `Split point added: "${newSplit.label}"`);
  };

  // Cancel pending split
  const handleCancelPendingSplit = () => {
    setPendingSplitIndex(null);
    setTempSplitLabel('');
  };

  // Remove a split point
  const handleRemoveSplit = (charIndex) => {
    setSplitPoints(prev => prev.filter(sp => sp.charIndex !== charIndex));
    setAdjustingSplitIndex(null);
  };

  // Find the start of the current or previous sentence
  const findPrevSentenceBoundary = (text, position) => {
    // Sentence endings: . ! ? followed by space or newline
    const sentenceEnders = /[.!?][\s\n]/g;
    let lastEnd = 0;
    let match;
    
    // Find all sentence endings before current position
    while ((match = sentenceEnders.exec(text)) !== null) {
      if (match.index + 2 >= position) break;
      lastEnd = match.index + 2; // Position after the punctuation and space
    }
    
    // If we're at the start of current sentence, find the previous one
    if (lastEnd >= position - 5) {
      // Search backwards for the sentence before that
      const textBefore = text.substring(0, Math.max(0, lastEnd - 2));
      const prevMatch = textBefore.match(/[.!?][\s\n]/g);
      if (prevMatch) {
        const lastPrevIndex = textBefore.lastIndexOf(prevMatch[prevMatch.length - 1]);
        return lastPrevIndex + 2;
      }
      return 0; // Return start of text
    }
    
    return lastEnd > 0 ? lastEnd : position;
  };

  // Find the start of the next sentence
  const findNextSentenceBoundary = (text, position) => {
    // Sentence endings: . ! ? followed by space or newline
    const sentenceEnders = /[.!?][\s\n]/g;
    sentenceEnders.lastIndex = position;
    
    const match = sentenceEnders.exec(text);
    if (match) {
      // Return position after the punctuation and space (start of next sentence)
      return Math.min(match.index + 2, text.length - 1);
    }
    
    return position; // Stay at current position if no sentence end found
  };

  // Adjust split position - move by words or sentences
  const handleAdjustSplit = (oldCharIndex, direction) => {
    if (!selectedSegment?.full_text) return;
    
    const text = selectedSegment.full_text;
    let newCharIndex;
    
    if (direction === 'prev') {
      // Move to previous word boundary
      newCharIndex = findPrevWordBoundary(text, oldCharIndex);
    } else if (direction === 'next') {
      // Move to next word boundary
      newCharIndex = findNextWordBoundary(text, oldCharIndex);
    } else if (direction === 'prevSentence') {
      // Move to start of current or previous sentence
      newCharIndex = findPrevSentenceBoundary(text, oldCharIndex);
    } else if (direction === 'nextSentence') {
      // Move to start of next sentence
      newCharIndex = findNextSentenceBoundary(text, oldCharIndex);
    } else {
      return;
    }
    
    // Ensure within bounds
    newCharIndex = Math.max(1, Math.min(text.length - 1, newCharIndex));
    
    // Check for collision with other splits
    const collision = splitPoints.some(sp => 
      sp.charIndex !== oldCharIndex && Math.abs(sp.charIndex - newCharIndex) < 10
    );
    if (collision) {
      notifyError('Too close to another split point');
      return;
    }
    
    setSplitPoints(prev => prev.map(sp => 
      sp.charIndex === oldCharIndex 
        ? { ...sp, charIndex: newCharIndex }
        : sp
    ));
    setAdjustingSplitIndex(newCharIndex);
  };

  // Get context around a split point for preview
  const getSplitContext = (charIndex) => {
    if (!selectedSegment?.full_text) return { before: '', after: '' };
    
    const text = selectedSegment.full_text;
    const before = text.substring(Math.max(0, charIndex - 40), charIndex);
    const after = text.substring(charIndex, Math.min(text.length, charIndex + 40));
    
    return { before, after };
  };

  // Render text with split markers and search highlights
  const renderTextWithSplitMarkers = () => {
    if (!selectedSegment?.full_text) return null;
    
    const text = selectedSegment.full_text;
    const sortedSplits = [...splitPoints].sort((a, b) => a.charIndex - b.charIndex);
    
    // If we have search results, highlight them
    if (searchQuery && searchResults.length > 0) {
      const parts = [];
      let lastIndex = 0;
      const searchLower = searchQuery.toLowerCase();
      const textLower = text.toLowerCase();
      let matchIndex = 0;
      let pos = 0;
      
      // Combine split markers and search highlights
      const allMarkers = [];
      
      // Add split markers
      sortedSplits.forEach(split => {
        allMarkers.push({ type: 'split', charIndex: split.charIndex, label: split.label });
      });
      
      // Add search highlights
      while ((pos = textLower.indexOf(searchLower, pos)) !== -1) {
        allMarkers.push({ 
          type: 'search', 
          charIndex: pos, 
          endIndex: pos + searchQuery.length,
          matchIndex: matchIndex,
          isCurrent: matchIndex === currentSearchIndex
        });
        matchIndex++;
        pos += 1;
      }
      
      // Sort all markers by position
      allMarkers.sort((a, b) => a.charIndex - b.charIndex);
      
      // Build the rendered parts
      allMarkers.forEach((marker, idx) => {
        // Add text before this marker
        if (marker.charIndex > lastIndex) {
          parts.push(<span key={`text-${idx}`}>{text.substring(lastIndex, marker.charIndex)}</span>);
        }
        
        if (marker.type === 'split') {
          const isAdjusting = adjustingSplitIndex === marker.charIndex;
          // Add split marker with adjustment controls
          // data-split-marker is used to exclude this element's text from character counting
          parts.push(
            <span 
              key={`split-${idx}`} 
              data-split-marker="true"
              className={`inline-flex items-center gap-1 mx-1 px-2 py-1 ${isAdjusting ? 'bg-orange-500' : 'bg-cyan-500'} text-white text-xs font-bold rounded`}
              style={{ fontFamily: 'Arial, sans-serif' }}
            >
              {isAdjusting && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleAdjustSplit(marker.charIndex, 'prevSentence'); }}
                  className="px-1 bg-white/20 rounded hover:bg-white/40"
                  title="Move to previous sentence"
                >◀◀</button>
              )}
              {isAdjusting && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleAdjustSplit(marker.charIndex, 'prev'); }}
                  className="px-1 bg-white/20 rounded hover:bg-white/40"
                  title="Move 1 word left"
                >◀</button>
              )}
              <span 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setAdjustingSplitIndex(isAdjusting ? null : marker.charIndex);
                }}
                className="cursor-pointer hover:underline"
                title={isAdjusting ? "Click to finish adjusting" : "Click to adjust position"}
              >
                ✂️ {marker.label}
              </span>
              {isAdjusting && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleAdjustSplit(marker.charIndex, 'next'); }}
                  className="px-1 bg-white/20 rounded hover:bg-white/40"
                  title="Move 1 word right"
                >▶</button>
              )}
              {isAdjusting && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleAdjustSplit(marker.charIndex, 'nextSentence'); }}
                  className="px-1 bg-white/20 rounded hover:bg-white/40"
                  title="Move to next sentence"
                >▶▶</button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); handleRemoveSplit(marker.charIndex); }}
                className="px-1 bg-red-600 rounded hover:bg-red-700 ml-1"
                title="Remove split"
              >✕</button>
            </span>
          );
          lastIndex = marker.charIndex;
        } else if (marker.type === 'search') {
          // Add search highlight
          const highlightClass = marker.isCurrent 
            ? 'bg-yellow-400 text-black font-bold px-0.5 rounded' 
            : 'bg-yellow-200 text-black px-0.5 rounded';
          parts.push(
            <mark 
              key={`search-${idx}`} 
              className={highlightClass}
              data-match-index={marker.matchIndex}
            >
              {text.substring(marker.charIndex, marker.endIndex)}
            </mark>
          );
          lastIndex = marker.endIndex;
        }
      });
      
      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(<span key="text-end">{text.substring(lastIndex)}</span>);
      }
      
      return parts;
    }
    
    // No search - just show splits
    if (sortedSplits.length === 0) {
      return text;
    }
    
    // Build segments with markers only
    const parts = [];
    let lastIndex = 0;
    
    sortedSplits.forEach((split, idx) => {
      // Add text before this split
      if (split.charIndex > lastIndex) {
        parts.push(
          <span key={`text-${idx}`}>{text.substring(lastIndex, split.charIndex)}</span>
        );
      }
      
      const isAdjusting = adjustingSplitIndex === split.charIndex;
      // Add split marker with adjustment controls
      // data-split-marker is used to exclude this element's text from character counting
      parts.push(
        <span 
          key={`marker-${idx}`} 
          data-split-marker="true"
          className={`inline-flex items-center gap-1 mx-1 px-2 py-1 ${isAdjusting ? 'bg-orange-500' : 'bg-cyan-500'} text-white text-xs font-bold rounded`}
          style={{ fontFamily: 'Arial, sans-serif' }}
        >
          {isAdjusting && (
            <button
              onClick={(e) => { e.stopPropagation(); handleAdjustSplit(split.charIndex, 'prevSentence'); }}
              className="px-1 bg-white/20 rounded hover:bg-white/40"
              title="Move to previous sentence"
            >◀◀</button>
          )}
          {isAdjusting && (
            <button
              onClick={(e) => { e.stopPropagation(); handleAdjustSplit(split.charIndex, 'prev'); }}
              className="px-1 bg-white/20 rounded hover:bg-white/40"
              title="Move 1 word left"
            >◀</button>
          )}
          <span 
            onClick={(e) => { 
              e.stopPropagation(); 
              setAdjustingSplitIndex(isAdjusting ? null : split.charIndex);
            }}
            className="cursor-pointer hover:underline"
            title={isAdjusting ? "Click to finish adjusting" : "Click to adjust position"}
          >
            ✂️ {split.label}
          </span>
          {isAdjusting && (
            <button
              onClick={(e) => { e.stopPropagation(); handleAdjustSplit(split.charIndex, 'next'); }}
              className="px-1 bg-white/20 rounded hover:bg-white/40"
              title="Move 1 word right"
            >▶</button>
          )}
          {isAdjusting && (
            <button
              onClick={(e) => { e.stopPropagation(); handleAdjustSplit(split.charIndex, 'nextSentence'); }}
              className="px-1 bg-white/20 rounded hover:bg-white/40"
              title="Move to next sentence"
            >▶▶</button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); handleRemoveSplit(split.charIndex); }}
            className="px-1 bg-red-600 rounded hover:bg-red-700 ml-1"
            title="Remove split"
          >✕</button>
        </span>
      );
      
      lastIndex = split.charIndex;
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(<span key="text-end">{text.substring(lastIndex)}</span>);
    }
    
    return parts;
  };

  // Show the first segment name prompt
  const handleShowFirstSegmentPrompt = () => {
    if (!selectedSegment || splitPoints.length === 0) {
      notifyError('Please add at least one split point');
      return;
    }
    setFirstSegmentLabel(selectedSegment.label || 'Introduction');
    setShowFirstSegmentPrompt(true);
  };

  // Apply the custom splits (called after first segment name is entered)
  const handleApplyCustomSplits = async () => {
    if (!selectedSegment || splitPoints.length === 0) {
      notifyError('Please add at least one split point');
      return;
    }

    if (!firstSegmentLabel.trim()) {
      notifyError('Please enter a name for the first segment');
      return;
    }

    setShowFirstSegmentPrompt(false);
    setSplitting(true);
    console.log('[Apply Splits] Starting with', splitPoints.length, 'split points');
    console.log('[Apply Splits] Segment ID:', selectedSegment.id);
    console.log('[Apply Splits] Manuscript ID:', manuscriptId);
    
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        notifyError('Not authenticated. Please log in again.');
        setSplitting(false);
        return;
      }
      
      // Get sorted character indices
      const sortedSplits = [...splitPoints].sort((a, b) => a.charIndex - b.charIndex);
      const charIndices = sortedSplits.map(sp => sp.charIndex);
      
      // Build labels array: first segment label + split point labels
      const labels = [firstSegmentLabel.trim(), ...sortedSplits.map(sp => sp.label)];
      
      console.log('[Apply Splits] Sending request:', { 
        url: `${API}/v3/manuscripts/${manuscriptId}/segments/${selectedSegment.id}/split`,
        charIndices, 
        labels 
      });
      
      const response = await axios.post(
        `${API}/v3/manuscripts/${manuscriptId}/segments/${selectedSegment.id}/split`,
        {
          method: 'custom',
          split_points: charIndices,
          labels: labels
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      console.log('[Apply Splits] Response:', response.data);
      
      console.log('[SUCCESS] ' + `Successfully split into ${response.data.new_segments?.length || splitPoints.length + 1} segments!`);
      
      // Exit split mode and close segment viewer
      handleExitClickToSplitMode();
      handleCloseSegmentView();
      
      // Refresh outline to show new segments
      await fetchOutline();
      
    } catch (error) {
      console.error('[Apply Splits] Error:', error);
      console.error('[Apply Splits] Error response:', error.response?.data);
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'object' 
        ? (errorDetail.message || errorDetail.error || 'Failed to split segment')
        : (errorDetail || error.message || 'Failed to split segment');
      notifyError(errorMsg);
    } finally {
      setSplitting(false);
    }
  };

  const handleSplitSegment = async (method = 'paragraphs') => {
    if (!selectedSegment) return;
    
    const confirmed = window.confirm(
      `Split "${selectedSegment.label}" by paragraphs?\n\nThis will create multiple segments and mark the original as deleted.`
    );
    
    if (!confirmed) return;
    
    setSplitting(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API}/v3/manuscripts/${manuscriptId}/segments/${selectedSegment.id}/split`,
        { method },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      console.log('[SPLIT] Segment split:', response.data);
      
      console.log('[SUCCESS] ' + response.data.message);
      
      // Close segment viewer
      handleCloseSegmentView();
      
      // Refresh outline to show new segments
      await fetchOutline();
      
    } catch (error) {
      console.error('[SPLIT] Error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.message || errorDetail.error || 'Failed to split segment')
        : (errorDetail || 'Failed to split segment');
      notifyError(errorMessage);
    } finally {
      setSplitting(false);
    }
  };

  const handleLabelChange = (segmentId, newLabel) => {
    setEditedSegments(prev => 
      prev.map(seg => 
        seg.id === segmentId ? { ...seg, label: newLabel } : seg
      )
    );
  };

  const handleTypeChange = (segmentId, newType) => {
    setEditedSegments(prev => 
      prev.map(seg => 
        seg.id === segmentId ? { ...seg, logical_type: newType } : seg
      )
    );
  };

  // P0 DIRECTIVE: handleIncludeInTTSChange removed - include_in_tts is deprecated

  const handleDeleteSegment = (segmentId) => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to remove this segment from the outline? It will be excluded from audio generation and will not appear in the player.')) {
      return;
    }
    
    // Mark segment as deleted
    setEditedSegments(prev => 
      prev.map(seg => 
        seg.id === segmentId 
          ? { ...seg, is_deleted: true } 
          : seg
      )
    );
    
    setHasUnsavedChanges(true);
    setSaveSuccess(false);
    console.log('[SUCCESS] ' + 'Segment marked for removal - click Save to apply changes');
  };

  // Move segment up in order
  // NOTE: audio_order controls PLAYBACK sequence (what users can reorder)
  // The 'order' field is the original NFD record index and should NOT be changed
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const visibleSegments = editedSegments.filter(seg => !seg.is_deleted).sort((a, b) => (a.audio_order ?? a.order) - (b.audio_order ?? b.order));
    const newSegments = [...visibleSegments];
    [newSegments[index - 1], newSegments[index]] = [newSegments[index], newSegments[index - 1]];
    // Update audio_order field (NOT order - that's the immutable NFD record index)
    newSegments.forEach((seg, idx) => {
      seg.audio_order = idx + 1;
    });
    // Merge back with deleted segments
    const deletedSegments = editedSegments.filter(seg => seg.is_deleted);
    setEditedSegments([...newSegments, ...deletedSegments]);
    setHasUnsavedChanges(true);
    setSaveSuccess(false);
    console.log('[SUCCESS] ' + 'Segment moved up - click Save to keep changes');
  };

  // Move segment down in order
  // NOTE: audio_order controls PLAYBACK sequence (what users can reorder)
  // The 'order' field is the original NFD record index and should NOT be changed
  const handleMoveDown = (index) => {
    const visibleSegments = editedSegments.filter(seg => !seg.is_deleted).sort((a, b) => (a.audio_order ?? a.order) - (b.audio_order ?? b.order));
    if (index >= visibleSegments.length - 1) return;
    const newSegments = [...visibleSegments];
    [newSegments[index], newSegments[index + 1]] = [newSegments[index + 1], newSegments[index]];
    // Update audio_order field (NOT order - that's the immutable NFD record index)
    newSegments.forEach((seg, idx) => {
      seg.audio_order = idx + 1;
    });
    // Merge back with deleted segments
    const deletedSegments = editedSegments.filter(seg => seg.is_deleted);
    setEditedSegments([...newSegments, ...deletedSegments]);
    setHasUnsavedChanges(true);
    setSaveSuccess(false);
    console.log('[SUCCESS] ' + 'Segment moved down - click Save to keep changes');
  };

  // Detect if segments have been refined or merged (content changed)
  const detectModifiedSegments = () => {
    const modified = [];
    let hasOrderChanges = false;
    
    // Compare each segment's character count to detect text changes
    for (const edited of editedSegments) {
      const original = originalSegments.find(o => o.id === edited.id);
      
      if (!original) {
        // New segment (shouldn't happen in normal editing, but handle it)
        console.log('[OUTLINE V3] New segment detected:', edited.id);
        modified.push(edited.id);
        continue;
      }
      
      // Check if order changed
      if (edited.order !== original.order) {
        console.log(`[OUTLINE V3] Segment ${edited.id} order changed: ${original.order} → ${edited.order}`);
        hasOrderChanges = true;
      }
      
      // Check if text content changed significantly
      const charCountDiff = Math.abs(edited.character_count - original.character_count);
      const percentChange = original.character_count > 0 
        ? (charCountDiff / original.character_count) * 100 
        : 0;
      
      // Only flag if character count changed by >1% (avoids TTS for minor typo fixes)
      if (percentChange > 1.0 || edited.manually_edited) {
        console.log(`[OUTLINE V3] Segment ${edited.id} modified: ${original.character_count} → ${edited.character_count} chars (${percentChange.toFixed(1)}% change)`);
        modified.push(edited.id);
      }
      
      // P0 DIRECTIVE: include_in_tts is DEPRECATED - removed check
    }
    
    // Check for deleted segments
    for (const original of originalSegments) {
      if (!editedSegments.find(e => e.id === original.id)) {
        console.log('[OUTLINE V3] Segment deleted:', original.id);
        // Deleted segments don't need TTS regeneration
      }
    }
    
    return { modified, hasOrderChanges };
  };

  // Save outline without closing - allows user to continue editing
  const handleSaveOnly = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      const token = localStorage.getItem('accessToken');
      
      // P0.9: Compute expected counts BEFORE commit
      const activeSegments = editedSegments.filter(s => !s.is_deleted);
      const deletedSegments = editedSegments.filter(s => s.is_deleted);
      const expectedActiveCount = activeSegments.length;
      const expectedRemovedCount = deletedSegments.length;
      
      // P0.9: Detect structural changes
      const hasStructuralChanges = editedSegments.some(seg => 
        seg.is_deleted || seg.is_removed || seg.removed_at || seg._split_generated || seg.audio_order_changed
      );
      
      console.log(`[P0.9 OUTLINE_COMMIT] expected_active=${expectedActiveCount} removed=${expectedRemovedCount} structural=${hasStructuralChanges}`);
      
      // Build payload with all segments
      const payload = {
        segments: editedSegments.map(seg => ({
          id: seg.id,
          logical_type: seg.logical_type,
          custom_label: seg.label !== seg.original_label ? seg.label : null,
          audio_order: seg.audio_order ?? seg.order,
          is_deleted: seg.is_deleted || false
        }))
      };
      
      // P0.9: Use the dedicated COMMIT endpoint - not PATCH
      const response = await axios.post(
        `${API}/publisher/manuscripts/${manuscriptId}/outline/commit`,
        payload,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );
      
      const receipt = response.data;
      
      console.log(`[P0.9 OUTLINE_COMMIT] Receipt:`, receipt);
      
      // P0.9: HARD CHECK - commit_ok must be true
      if (!receipt.commit_ok) {
        console.error(`[P0.9 OUTLINE_COMMIT] ❌ COMMIT FAILED:`, receipt);
        notifyError(`Outline commit failed: ${receipt.reason || receipt.error}. No changes were applied.`);
        return;
      }
      
      // P0.9: Verify receipt matches expected
      if (receipt.active_count !== expectedActiveCount) {
        console.error(`[P0.9 OUTLINE_COMMIT] ❌ MISMATCH: expected=${expectedActiveCount} receipt=${receipt.active_count}`);
        notifyError(`Outline commit verification failed. Expected ${expectedActiveCount} segments but canonical has ${receipt.active_count}.`);
        return;
      }
      
      console.log(`[P0.9 OUTLINE_COMMIT] ✅ VERIFIED: active=${receipt.active_count} hash=${receipt.audio_scope_hash} version=${receipt.audio_scope_version}`);
      
      setHasUnsavedChanges(false);
      setLastSaveTime(new Date());
      setSaveSuccess(true);
      setAutoSaveStatus(null);
      clearLocalBackup();
      
      // Update outline state with receipt values
      setOutline(prev => prev ? {
        ...prev,
        canonical_outline_saved: true,
        segment_count: receipt.active_count,
        audio_scope_version: receipt.audio_scope_version,
        audio_scope_hash: receipt.audio_scope_hash
      } : prev);
      
      // Update original segments to reflect saved state
      setOriginalSegments(JSON.parse(JSON.stringify(editedSegments)));
      
      // Clear success indicator after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error) {
      console.error('[P0.9 OUTLINE_COMMIT] Error:', error);
      
      // Check for specific commit failure
      const errorData = error.response?.data;
      if (errorData?.commit_ok === false) {
        notifyError(`Outline commit failed: ${errorData.reason || 'Unknown error'}. Try again when backend is healthy.`);
        return;
      }
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        notifyError('Commit timed out. Your changes are backed up locally - please try again.');
      } else {
        const errorDetail = error.response?.data?.detail;
        const errorMessage = typeof errorDetail === 'object' 
          ? (errorDetail.message || errorDetail.error || 'Failed to commit outline.')
          : (errorDetail || 'Failed to commit outline.');
        notifyError(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      
      // P0.9: Compute expected counts BEFORE commit
      const activeSegments = editedSegments.filter(s => !s.is_deleted);
      const deletedSegments = editedSegments.filter(s => s.is_deleted);
      const expectedActiveCount = activeSegments.length;
      const expectedRemovedCount = deletedSegments.length;
      
      // P0.9: Detect structural changes
      const hasStructuralChanges = editedSegments.some(seg => 
        seg.is_deleted || seg.is_removed || seg.removed_at || seg._split_generated || seg.audio_order_changed
      );
      
      console.log(`[P0.9 OUTLINE_COMMIT] expected_active=${expectedActiveCount} removed=${expectedRemovedCount} structural=${hasStructuralChanges}`);
      
      // Detect which segments need TTS regeneration and if order changed
      const { modified: modifiedSegmentIds, hasOrderChanges } = detectModifiedSegments();
      
      // Check for NEW segments created in THIS session (from click-to-split) that need TTS
      const originalSegmentIds = new Set(originalSegments.map(seg => seg.id));
      const newSegmentsNeedingTTS = editedSegments
        .filter(seg => 
          !seg.is_deleted &&
          !seg.has_audio &&
          !originalSegmentIds.has(seg.id)
        )
        .map(seg => seg.id);
      
      // For POST-INGESTION (standalone) mode: Check ALL segments missing audio
      const segmentsWithIncompleteAudio = isStandalone 
        ? editedSegments
            .filter(seg => !seg.is_deleted && !seg.has_audio)
            .map(seg => seg.id)
        : [];
      
      const allSegmentsNeedingTTS = [...new Set([
        ...modifiedSegmentIds, 
        ...newSegmentsNeedingTTS,
        ...segmentsWithIncompleteAudio
      ])];
      const needsTTSRegeneration = allSegmentsNeedingTTS.length > 0;
      
      console.log('[OUTLINE V3] All segments needing TTS:', allSegmentsNeedingTTS);
      console.log('[OUTLINE V3] Has order changes:', hasOrderChanges);
      
      // Build payload
      const payload = {
        segments: editedSegments.map(seg => ({
          id: seg.id,
          logical_type: seg.logical_type,
          custom_label: seg.label !== seg.original_label ? seg.label : null,
          audio_order: seg.audio_order ?? seg.order,
          is_deleted: seg.is_deleted || false
        }))
      };
      
      // P0.9: Use the dedicated COMMIT endpoint
      const response = await axios.post(
        `${API}/publisher/manuscripts/${manuscriptId}/outline/commit`,
        payload,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );
      
      const receipt = response.data;
      
      console.log(`[P0.9 OUTLINE_COMMIT] Receipt:`, receipt);
      
      // P0.9: HARD CHECK - commit_ok must be true
      if (!receipt.commit_ok) {
        console.error(`[P0.9 OUTLINE_COMMIT] ❌ COMMIT FAILED:`, receipt);
        notifyError(`Outline commit failed: ${receipt.reason || receipt.error}. No changes were applied. Try again when backend is healthy.`);
        return;
      }
      
      // P0.9: Verify receipt matches expected
      if (receipt.active_count !== expectedActiveCount) {
        console.error(`[P0.9 OUTLINE_COMMIT] ❌ MISMATCH: expected=${expectedActiveCount} receipt=${receipt.active_count}`);
        notifyError(`Outline commit verification failed. Expected ${expectedActiveCount} segments but canonical has ${receipt.active_count}. Audio generation blocked.`);
        return;
      }
      
      console.log(`[P0.9 OUTLINE_COMMIT] ✅ VERIFIED: active=${receipt.active_count} hash=${receipt.audio_scope_hash} version=${receipt.audio_scope_version}`);
      
      // Clear backup and reset state
      clearLocalBackup();
      setHasUnsavedChanges(false);
      setAutoSaveStatus(null);
      
      // Update outline state with receipt values
      setOutline(prev => prev ? {
        ...prev,
        canonical_outline_saved: true,
        segment_count: receipt.active_count,
        audio_scope_version: receipt.audio_scope_version,
        audio_scope_hash: receipt.audio_scope_hash
      } : prev);
      
      // Calculate total character count
      const totalCharacterCount = editedSegments
        .filter(seg => !seg.is_deleted)
        .reduce((sum, seg) => sum + (seg.character_count || 0), 0);
      
      // P0.9: Pass commit receipt to callback for TTS
      const commitReceipt = {
        commit_ok: true,
        active_count: receipt.active_count,
        audio_scope_version: receipt.audio_scope_version,
        audio_scope_hash: receipt.audio_scope_hash
      };
      
      // Determine if we should trigger TTS
      if (isStandalone) {
        console.log('[OUTLINE V3] Standalone mode - segments needing TTS:', allSegmentsNeedingTTS.length);
        
        if (onSaveAndContinue) {
          onSaveAndContinue({ 
            needsTTS: needsTTSRegeneration,
            modifiedSegments: allSegmentsNeedingTTS,
            incompleteSegments: segmentsWithIncompleteAudio.length,
            hasOrderChanges,
            newSegmentsFromSplit: newSegmentsNeedingTTS.length,
            totalCharacterCount,
            commitReceipt
          });
        }
      } else if (onSaveAndContinue) {
        if (needsTTSRegeneration) {
          console.log('[OUTLINE V3] Triggering TTS for new/modified segments');
          onSaveAndContinue({ 
            needsTTS: true, 
            modifiedSegments: allSegmentsNeedingTTS,
            hasOrderChanges,
            newSegmentsFromSplit: newSegmentsNeedingTTS.length,
            totalCharacterCount,
            commitReceipt
          });
        } else {
          onSaveAndContinue({ needsTTS: false, hasOrderChanges, totalCharacterCount, commitReceipt });
        }
      }
      
      // Update original segments to reflect saved state
      setOriginalSegments(JSON.parse(JSON.stringify(editedSegments)));
      
    } catch (error) {
      console.error('[P0.9 OUTLINE_COMMIT] Error:', error);
      
      // Check for specific commit failure
      const errorData = error.response?.data;
      if (errorData?.commit_ok === false) {
        notifyError(`Outline commit failed: ${errorData.reason || 'Unknown error'}. No changes were applied.`);
        return;
      }
      
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.message || errorDetail.error || 'Failed to commit outline')
        : (errorDetail || 'Failed to commit outline');
      notifyError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'front_matter':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'chapter':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'back_matter':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'part':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  return (
    <>
      {/* Main Outline Modal */}
      <Dialog open={isOpen && !selectedSegment} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[85vh] overflow-hidden flex flex-col mx-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <BookOpen className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              <div>
                <DialogTitle className="text-xl">Edit Outline</DialogTitle>
                {outline && (
                  <DialogDescription>
                    {outline.title} by {outline.author}
                  </DialogDescription>
                )}
              </div>
            </div>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : outline ? (
            <div className="flex-1 overflow-y-auto">
              {/* Summary Stats - P0.7 FIX: Calculate from EDITED segments, not original outline */}
              {(() => {
                // P0.7: Compute metrics from current edited state (active segments only)
                const activeSegments = editedSegments.filter(s => !s.is_deleted);
                const liveSegmentCount = activeSegments.length;
                const liveWordCount = activeSegments.reduce((sum, s) => sum + (s.word_count || 0), 0);
                const liveCharCount = activeSegments.reduce((sum, s) => sum + (s.character_count || 0), 0);
                const deletedCount = editedSegments.filter(s => s.is_deleted).length;
                
                // Detect if counts differ from original (for change indication)
                const countsChanged = liveSegmentCount !== outline.segment_count;
                
                return (
                  <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Segments</div>
                      <div className={`text-2xl font-bold ${countsChanged ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
                        {liveSegmentCount}
                        {deletedCount > 0 && (
                          <span className="text-sm font-normal text-red-500 ml-2">
                            (-{deletedCount})
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Total Words</div>
                      <div className={`text-2xl font-bold ${countsChanged ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
                        {liveWordCount.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Characters</div>
                      <div className={`text-2xl font-bold ${countsChanged ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
                        {liveCharCount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* P1.6: Canonicalization Status Banner - P0.7: Show when hasUnsavedChanges OR canonical not saved */}
              {(hasUnsavedChanges || outline.canonical_outline_saved === false) && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg" data-testid="canonicalization-banner">
                  <div className="flex items-center gap-2">
                    <Save className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <span className="font-medium">Changes pending.</span>{' '}
                      Save outline to finalize book structure and enable audio generation.
                    </p>
                  </div>
                </div>
              )}

              {/* Large Segment Warning - P0.7: Use editedSegments not outline.segments */}
              {editedSegments.filter(s => !s.is_deleted).some(s => s.word_count > 10000) && (
                <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        Large Segment Detected
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                        One or more segments exceed 10,000 words. Consider using &ldquo;Click to Split&rdquo; to break them into smaller chunks for more efficient TTS processing and better navigation.
                      </p>
                      <ul className="text-xs text-amber-600 dark:text-amber-400 mt-2 space-y-1">
                        {editedSegments.filter(s => !s.is_deleted && s.word_count > 10000).map(s => (
                          <li key={s.id}>• {s.label}: {s.word_count.toLocaleString()} words</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Segments List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Segments (Editable)
                  </h3>
                  {selectedSegments.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedSegments.length} selected
                      </span>
                      <Button
                        onClick={handleMergeSegments}
                        disabled={selectedSegments.length < 2 || merging}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {merging ? (
                          <>
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Merging...
                          </>
                        ) : (
                          `Merge ${selectedSegments.length}`
                        )}
                      </Button>
                      <Button
                        onClick={() => setSelectedSegments([])}
                        size="sm"
                        variant="outline"
                        className="text-gray-600"
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                </div>
                {editedSegments
                  .filter(seg => !seg.is_deleted)
                  .sort((a, b) => (a.audio_order ?? a.order) - (b.audio_order ?? b.order))
                  .map((segment, index, filteredArray) => (
                  <div
                    key={segment.id}
                    className={`p-4 border rounded-lg transition-all ${
                      selectedSegments.includes(segment.id)
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 ring-2 ring-purple-200 dark:ring-purple-800'
                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Reorder Arrows */}
                      <div className="flex flex-col items-center gap-0.5 flex-shrink-0 pt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:text-purple-400 dark:hover:bg-purple-900/30 disabled:opacity-30"
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <span className="text-xs font-bold text-gray-400 dark:text-gray-500 w-6 text-center">
                          {segment.order}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:text-purple-400 dark:hover:bg-purple-900/30 disabled:opacity-30"
                          onClick={() => handleMoveDown(index)}
                          disabled={index === filteredArray.length - 1}
                          title="Move down"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Selection Checkbox */}
                      <Checkbox
                        checked={selectedSegments.includes(segment.id)}
                        onCheckedChange={() => toggleSegmentSelection(segment.id)}
                        className="mt-3"
                      />
                      
                      <div className="flex-1 space-y-2 min-w-0">
                        {/* Label Input */}
                        <div>
                          <Input
                            value={segment.label}
                            onChange={(e) => handleLabelChange(segment.id, e.target.value)}
                            className="font-semibold text-gray-900 dark:text-white"
                            placeholder="Segment label"
                          />
                          {segment.label !== segment.original_label && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                              Original: &ldquo;{segment.original_label}&rdquo;
                            </p>
                          )}
                        </div>

                        {/* Type Selector and Stats */}
                        <div className="flex items-center gap-3 flex-wrap">
                          <select
                            value={segment.logical_type}
                            onChange={(e) => handleTypeChange(segment.id, e.target.value)}
                            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="front_matter">Front Matter</option>
                            <option value="chapter">Chapter</option>
                            <option value="part">Part</option>
                            <option value="appendix">Appendix</option>
                            <option value="back_matter">Back Matter</option>
                            <option value="list">List</option>
                            <option value="other">Other</option>
                          </select>
                          
                          <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                            {segment.word_count.toLocaleString()} words
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                            {segment.character_count.toLocaleString()} chars
                          </span>
                        </div>

                        {/* P0 DIRECTIVE: include_in_tts checkbox REMOVED - field is deprecated */}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-gray-600 hover:text-purple-600 hover:bg-purple-50 dark:text-gray-300 dark:hover:text-purple-400 dark:hover:bg-purple-900/30"
                          onClick={() => handleSegmentClick(segment)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                          onClick={() => handleDeleteSegment(segment.id)}
                          title="Remove segment"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No outline data available
            </div>
          )}

          {/* Status indicators and buttons */}
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Status indicators */}
            <div className="flex items-center justify-center mb-4 min-h-[24px]">
              {autoSaveStatus === 'saving' && (
                <span className="text-cyan-600 dark:text-cyan-400 text-sm flex items-center gap-1">
                  <Loader2 className="w-4 h-4 animate-spin" /> Auto-saving...
                </span>
              )}
              {autoSaveStatus === 'saved' && (
                <span className="text-green-600 dark:text-green-400 text-sm flex items-center gap-1">
                  <Check className="w-4 h-4" /> Auto-saved
                </span>
              )}
              {autoSaveStatus === 'error' && (
                <span className="text-red-600 dark:text-red-400 text-sm">⚠ Auto-save failed - please save manually</span>
              )}
              {!autoSaveStatus && hasUnsavedChanges && !saving && (
                <span className="text-amber-600 dark:text-amber-400 text-sm">● Unsaved changes (auto-saves in 2 min)</span>
              )}
              {!autoSaveStatus && saveSuccess && (
                <span className="text-green-600 dark:text-green-400 text-sm flex items-center gap-1">
                  <Check className="w-4 h-4" /> Saved successfully
                </span>
              )}
              {!autoSaveStatus && lastSaveTime && !saveSuccess && !hasUnsavedChanges && (
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  Last saved: {lastSaveTime.toLocaleTimeString()}
                </span>
              )}
            </div>
            
            {/* Button row - P0: Centered main action with safe spacing */}
            <div className="flex flex-col items-center gap-4 pt-4">
              {/* Primary Action - Centered with prominent styling */}
              <Button 
                onClick={handleSaveChanges} 
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white px-12 py-3 text-base font-medium"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isStandalone ? 'Update Outline' : 'Save and Continue'}
                  </>
                )}
              </Button>
              
              {/* Secondary Actions - Centered row below with more spacing */}
              <div className="flex gap-6 items-center justify-center">
                <Button 
                  onClick={handleSaveOnly} 
                  disabled={saving || !hasUnsavedChanges}
                  variant="outline"
                  className="text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                  title="Save changes and continue editing"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Draft
                </Button>
                
                <Button 
                  onClick={() => {
                    // Continue without saving
                    if (onSaveAndContinue) {
                      onSaveAndContinue();
                    } else {
                      onClose();
                    }
                  }}
                  variant="ghost" 
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Segment Text Viewer Modal */}
      <Dialog open={!!selectedSegment} onOpenChange={handleCloseSegmentView}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[85vh] overflow-hidden flex flex-col mx-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>{selectedSegment?.label}</DialogTitle>
                <DialogDescription>
                  {selectedSegment?.word_count.toLocaleString()} words · {selectedSegment?.character_count.toLocaleString()} characters
                </DialogDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={handleCloseSegmentView}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          {segmentLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : selectedSegment ? (
            <div className="flex-1 overflow-y-auto min-h-0">
              {isEditingText ? (
                <div className="space-y-4">
                  <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                    ⚠️ Editing this text will require TTS regeneration for this segment. Changes will be saved to the manuscript.
                  </div>
                  <textarea
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    className="w-full min-h-[300px] max-h-[500px] p-4 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 resize-y focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter segment text..."
                  />
                </div>
              ) : isClickToSplitMode ? (
                /* Click-to-Split Mode UI - Full Text with Click Detection */
                <div className="flex flex-col h-full">
                  {/* Instructions */}
                  <div className="text-sm text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 p-3 rounded-lg flex items-center gap-2 mb-3 flex-shrink-0">
                    <Scissors className="h-4 w-4" />
                    <span><strong>Click-to-Split Mode:</strong> Use search to find chapter breaks, then click in the text to add split points.</span>
                  </div>
                  
                  {/* Search Bar */}
                  <div className="flex items-center gap-2 mb-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg flex-shrink-0">
                    <Search className="w-4 h-4 text-gray-500" />
                    <Input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      placeholder="Search for 'Chapter', 'Part', or any text..."
                      className="flex-1 bg-white dark:bg-gray-900"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleNextSearchResult();
                        }
                      }}
                    />
                    {searchResults.length > 0 && (
                      <>
                        <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {currentSearchIndex + 1} of {searchResults.length}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handlePrevSearchResult}
                          className="px-2"
                          title="Previous match"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleNextSearchResult}
                          className="px-2"
                          title="Next match"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {searchQuery && searchResults.length === 0 && (
                      <span className="text-sm text-red-500">No matches</span>
                    )}
                  </div>

                  {/* Stats bar */}
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3 flex-shrink-0">
                    <span><strong>{selectedSegment?.character_count?.toLocaleString()}</strong> characters</span>
                    <span><strong className="text-cyan-600 dark:text-cyan-400">{splitPoints.length}</strong> split points</span>
                    <span><strong className="text-green-600 dark:text-green-400">{splitPoints.length + 1}</strong> segments after split</span>
                  </div>

                  {/* Pending split input */}
                  {pendingSplitIndex !== null && (
                    <div className="bg-cyan-100 dark:bg-cyan-900/50 border-2 border-cyan-500 rounded-lg p-4 mb-3 flex-shrink-0">
                      <div className="text-sm text-cyan-800 dark:text-cyan-200 mb-2">
                        <strong>Split at character {pendingSplitIndex.toLocaleString()}</strong>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={tempSplitLabel}
                          onChange={(e) => setTempSplitLabel(e.target.value)}
                          placeholder="Enter segment name (e.g., Chapter 2)..."
                          className="flex-1 bg-white dark:bg-gray-800"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleConfirmSplit();
                            if (e.key === 'Escape') handleCancelPendingSplit();
                          }}
                        />
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-500 text-white"
                          onClick={handleConfirmSplit}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Add Split
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelPendingSplit}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Full text - clickable */}
                  <div 
                    ref={textContainerRef}
                    className="flex-1 overflow-y-auto border border-gray-300 rounded-lg p-6 bg-white cursor-text select-text"
                    onClick={handleTextClick}
                    style={{ minHeight: '500px', maxHeight: '70vh' }}
                  >
                    <div 
                      className="whitespace-pre-wrap leading-relaxed"
                      style={{ 
                        fontFamily: '"Times New Roman", Times, serif',
                        fontSize: '16px',
                        color: '#000000',
                        lineHeight: '1.8'
                      }}
                    >
                      {renderTextWithSplitMarkers()}
                    </div>
                  </div>

                  {/* Split points - compact bar at bottom */}
                  {splitPoints.length > 0 && (
                    <div className="mt-2 p-2 bg-cyan-50 border border-cyan-200 rounded flex items-center gap-2 flex-shrink-0 overflow-x-auto">
                      <span className="text-xs font-medium text-cyan-700 whitespace-nowrap">Splits ({splitPoints.length}):</span>
                      {splitPoints.map((sp, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-500 text-white text-xs rounded cursor-pointer hover:bg-red-500 transition-colors whitespace-nowrap"
                          onClick={() => handleRemoveSplit(sp.charIndex)}
                          title="Click to remove"
                        >
                          {idx + 2}. {sp.label}
                          <X className="w-3 h-3" />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="prose dark:prose-invert max-w-none mb-4">
                    <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200 leading-relaxed">
                      {selectedSegment.full_text}
                    </div>
                  </div>
                  {selectedSegment.manually_edited && (
                    <div className="text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                      ℹ️ This segment has been manually edited
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}

          {/* Footer: Button positions swapped to avoid cursor overlap with Save and Continue */}
          <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            {/* Back to Outline - LEFT side to avoid cursor landing on Save and Continue */}
            <Button onClick={handleCloseSegmentView} variant="outline" className="text-black dark:text-white">
              Back to Outline
            </Button>
            <div className="flex gap-2">
              {isEditingText ? (
                <>
                  <Button 
                    onClick={handleCancelEditingText}
                    variant="outline"
                    disabled={savingText}
                    className="text-black dark:text-white"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveSegmentText}
                    disabled={savingText || !editedText.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {savingText ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </>
              ) : isClickToSplitMode ? (
                <>
                  {/* First Segment Name Prompt */}
                  {showFirstSegmentPrompt && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          Name the First Segment
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                          Enter a name for the first segment (the content before your first split point):
                        </p>
                        <Input
                          type="text"
                          value={firstSegmentLabel}
                          onChange={(e) => setFirstSegmentLabel(e.target.value)}
                          placeholder="e.g., Introduction, Chapter 1..."
                          className="mb-4"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && firstSegmentLabel.trim()) {
                              handleApplyCustomSplits();
                            }
                            if (e.key === 'Escape') {
                              setShowFirstSegmentPrompt(false);
                            }
                          }}
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => setShowFirstSegmentPrompt(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleApplyCustomSplits}
                            disabled={!firstSegmentLabel.trim() || splitting}
                            className="bg-cyan-600 hover:bg-cyan-500 text-white"
                          >
                            {splitting ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Applying...
                              </>
                            ) : (
                              'Apply Splits'
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  <Button 
                    onClick={handleExitClickToSplitMode}
                    variant="outline"
                    className="text-black dark:text-white"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleShowFirstSegmentPrompt}
                    disabled={splitting || splitPoints.length === 0}
                    className="bg-cyan-600 hover:bg-cyan-500 text-white"
                  >
                    {splitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Splitting...
                      </>
                    ) : (
                      <>
                        <Scissors className="h-4 w-4 mr-2" />
                        Apply Splits ({splitPoints.length + 1} segments)
                      </>
                    )}
                  </Button>
                </>
              ) : selectedSegment ? (
                <>
                  <Button 
                    onClick={handleStartEditingText}
                    variant="outline"
                    className="text-purple-600 dark:text-purple-400 border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                  >
                    Edit Text
                  </Button>
                  <Button 
                    onClick={handleEnterClickToSplitMode}
                    variant="outline"
                    className="text-cyan-600 dark:text-cyan-400 border-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                  >
                    <Scissors className="h-4 w-4 mr-1" />
                    Click to Split
                  </Button>
                  {/* Split by Paragraphs option removed per Plan C requirements */}
                </>
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OutlineViewerModalV3;
